/**
 * Configurações da gravação.
 *
 * Ficam no navegador (localStorage), não no banco: são preferências da MÁQUINA de
 * quem grava — qual microfone, quanta banda, se a tela da aba pode ser amostrada.
 * A mesma pessoa no notebook e no desktop quer respostas diferentes, e sincronizar
 * isso pelo servidor só criaria o defeito de o microfone escolhido não existir na
 * outra máquina.
 *
 * Ler configuração desconhecida é normal (versão antiga da tela): a mistura com o
 * padrão garante que campo novo nasce no lugar certo em vez de `undefined` vazando
 * para dentro do MediaRecorder.
 */

const CHAVE = "crm:reuniao:config";

export type ConfigGravacao = {
  /** `""` = microfone padrão do sistema. */
  microfoneId: string;
  /**
   * As duas fontes, guardadas separadas porque é isso que o gravador consome.
   *
   * A TELA não mostra dois interruptores: mostra um MODO (`modoDe`/`comModo`).
   * Dois interruptores independentes permitiam desligar os dois, e aí o clique em
   * gravar só falhava depois — erro que a interface tinha como não deixar acontecer.
   */
  gravarMic: boolean;
  gravarAba: boolean;
  /**
   * Como chamar o outro lado na transcrição.
   *
   * Era fixo "Participantes", que é o vocabulário de ferramenta de reunião. Num
   * escritório o outro lado é o CLIENTE, e o nome certo aparece em cada fala da
   * transcrição, do resumo e da peça. Quando a leitura de tela acha o nome próprio,
   * ele vence este rótulo.
   */
  rotuloOutroLado: string;
  /** 64 kbps em Opus é fala limpa. 128 dobra o upload sem melhorar a transcrição. */
  bitrate: number;
  /** Idioma do áudio. `auto` deixa o modelo decidir (pior em áudio curto). */
  idioma: string;
  /** Ler o nome de quem fala na tela do Meet/Teams (custa chamada de modelo). */
  lerNomes: boolean;
  /** Intervalo de amostragem da tela, em segundos. */
  intervaloQuadro: number;
  /** Resumir sozinho quando a transcrição chegar. */
  resumirAoTerminar: boolean;
  /**
   * Extrair a tese sozinho quando a transcrição chegar.
   *
   * Só vale para reunião vinculada a um cliente: a extração é a proposta de tese,
   * datas e provas DELE. Continua sendo proposta — aplicar na ficha é clique de
   * quem decide. Custa uma chamada de modelo, então é escolha, não padrão.
   */
  extrairAoTerminar: boolean;
  /**
   * Transcrever janelas curtas DURANTE a reunião.
   *
   * Custa uma transcrição por janela — o valor é não terminar uma hora de reunião
   * para descobrir que o áudio saiu mudo. Desligado por padrão.
   */
  previaAoVivo: boolean;
  janelaPrevia: number;
  /**
   * Guardar o áudio para poder ouvir de novo.
   *
   * Ligado, a reunião fica reproduzível e o texto acompanha a fala palavra por
   * palavra. Desligado, o áudio é descartado depois de virar texto — a escolha
   * mais conservadora, porque conversa com cliente é passivo, não acervo.
   */
  guardarAudio: boolean;
  /**
   * Guardar também o VÍDEO da aba compartilhada.
   *
   * Desligado por padrão, e não por economia de código: é o arquivo mais pesado e
   * o passivo mais delicado que a reunião produz — imagem de gente, do documento
   * que estava na tela, de quem mais estava na chamada. Vale quando VER a reunião
   * importa (o cliente mostrou o cartão de ponto na tela), e aí é escolha
   * consciente de quem grava, feita antes de começar.
   */
  guardarVideo: boolean;
};

export const PADRAO: ConfigGravacao = {
  microfoneId: "",
  gravarMic: true,
  /*
   * Desligado por padrão desde que o gravador passou a morar DENTRO do
   * atendimento: ali a reunião é o advogado ao telefone com o cliente, e o
   * primeiro clique não pode abrir um diálogo de compartilhar tela. Quem grava
   * um Meet liga "Chamada online" nos ajustes — e a escolha fica guardada.
   */
  gravarAba: false,
  rotuloOutroLado: "Cliente",
  bitrate: 64_000,
  idioma: "pt",
  lerNomes: true,
  intervaloQuadro: 4,
  resumirAoTerminar: false,
  extrairAoTerminar: false,
  previaAoVivo: false,
  janelaPrevia: 45,
  guardarAudio: true,
  guardarVideo: false,
};

/**
 * O modo, que é como a pessoa pensa antes de gravar: chamada online, conversa na
 * sala, ou só ouvindo o outro lado.
 */
export type Modo = "chamada" | "presencial" | "so-outro";

export const MODOS: { id: Modo; rotulo: string; ajuda: string }[] = [
  {
    id: "chamada",
    rotulo: "Chamada online",
    ajuda: "Sua voz e o áudio da aba em trilhas separadas — é assim que sai quem falou o quê.",
  },
  {
    id: "presencial",
    rotulo: "Só meu microfone",
    ajuda: "Reunião na sala, ou aba sem áudio: transcrição corrida, sem atribuição.",
  },
  { id: "so-outro", rotulo: "Só o áudio da aba", ajuda: "Você só assiste: entra o que vem da chamada, sem a sua voz." },
];

export const modoDe = (c: Pick<ConfigGravacao, "gravarMic" | "gravarAba">): Modo =>
  c.gravarMic && c.gravarAba ? "chamada" : c.gravarAba ? "so-outro" : "presencial";

/** Nunca devolve as duas fontes desligadas — o estado que travava o botão gravar. */
export const comModo = (c: ConfigGravacao, modo: Modo): ConfigGravacao => ({
  ...c,
  gravarMic: modo !== "so-outro",
  gravarAba: modo !== "presencial",
});

/**
 * Quanto tempo de reunião cabe num arquivo, dado o teto do provedor.
 *
 * O OpenRouter recusa multipart acima de 25 MB, e o número em kbps não diz nada
 * sobre isso: "64 kbps" e "cabe 52 min" são a mesma informação, mas só a segunda
 * responde à pergunta que a pessoa tem antes de uma reunião longa.
 */
export const minutosQueCabem = (bitrate: number, limiteMB = 25): number =>
  /* MB decimal (10^6), não MiB: o provedor diz "25 MB" sem dizer qual, e a conta
     conservadora nunca promete mais tempo do que o upload aceita. */
  Math.floor((limiteMB * 1_000_000 * 8) / (Math.max(1, bitrate) * 60));

export const IDIOMAS = [
  { id: "pt", rotulo: "Português (Brasil)" },
  { id: "en", rotulo: "Inglês" },
  { id: "es", rotulo: "Espanhol" },
  { id: "auto", rotulo: "Detectar automaticamente" },
];

/** "1h44" — o que cabe no limite do provedor, dito dentro da própria opção. */
export const tempoQueCabe = (bitrate: number): string => {
  const min = minutosQueCabem(bitrate);
  return `${Math.floor(min / 60)}h${String(min % 60).padStart(2, "0")}`;
};

export const BITRATES = [32_000, 64_000, 128_000].map((b) => ({
  id: String(b),
  rotulo: `${b / 1000} kbps · cabe ${tempoQueCabe(b)}`,
}));

export const INTERVALOS = [
  { id: "3", rotulo: "Amostra fina — pega troca rápida de falante" },
  { id: "4", rotulo: "Equilibrada" },
  { id: "8", rotulo: "Econômica — menos quadros, menos custo" },
];

export const JANELAS = [
  { id: "30", rotulo: "Aparece a cada 30s" },
  { id: "45", rotulo: "Aparece a cada 45s" },
  { id: "90", rotulo: "Aparece a cada 90s — frase menos cortada" },
];

export const lerConfig = (): ConfigGravacao => {
  try {
    const salvo = JSON.parse(localStorage.getItem(CHAVE) ?? "{}");
    return { ...PADRAO, ...(salvo && typeof salvo === "object" ? salvo : {}) };
  } catch {
    return { ...PADRAO };
  }
};

export const salvarConfig = (config: Partial<ConfigGravacao>): ConfigGravacao => {
  const inteira = { ...PADRAO, ...config };
  try {
    localStorage.setItem(CHAVE, JSON.stringify(inteira));
  } catch {
    /* aba em modo restrito: a configuração vale para esta sessão e pronto */
  }
  return inteira;
};

/** Lista os microfones. O nome só aparece depois de a pessoa dar permissão. */
export const listarMicrofones = async (): Promise<{ id: string; rotulo: string }[]> => {
  try {
    const dispositivos = await navigator.mediaDevices.enumerateDevices();
    return dispositivos
      .filter((d) => d.kind === "audioinput")
      .map((d, i) => ({ id: d.deviceId, rotulo: d.label || `Microfone ${i + 1}` }));
  } catch {
    return [];
  }
};
